import {
  FILTERS,
  selectedFilters,
  getFilterOptions,
  filterItems,
  setFilterSelected,
  clearAllFilters,
  replaceSelectedFilters,
  hasSelectedFilters,
} from './gallery-filters';

const FILTER_PANEL_ID = 'inspiration-gallery-filter-panel';
const HEADER_MOBILE_MENU_ID = 'headerNewMobileMenu';

let filtersReady = false;
let filterBarRenderedReady = false;
let pendingModalItemId = null;

const readyFullImages = new Set();

const escapeHtml = (value) => {
  if (!value) {
    return '';
  }
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

const getRoot = () => document.getElementById('media-gallery-root');

const getFilterPanelEl = () => document.getElementById(FILTER_PANEL_ID);

const isFilterPanelOpen = () => (
  Boolean(getFilterPanelEl()?.classList.contains('toggled'))
);

const isHeaderMobileMenuOpen = () => (
  Boolean(document.getElementById(HEADER_MOBILE_MENU_ID)?.classList.contains('show'))
);

const closeHeaderMobileMenu = () => {
  const menu = document.getElementById(HEADER_MOBILE_MENU_ID);
  if (!menu || typeof bootstrap === 'undefined') {
    return;
  }
  bootstrap.Collapse.getOrCreateInstance(menu, { toggle: false }).hide();
};

// Dual-overlay scroll-lock: do not remove without coordinating
// HeaderNewBlock.cshtml (headerNewMobileMenu). Both overlays set
// body.style.overflow; unlocking here must skip clearing overflow while the
// header mobile menu is still open, or closing the gallery filter panel would
// re-enable page scroll under an open nav overlay.
const setBodyScrollLocked = (locked) => {
  document.body.classList.toggle('overflow-hidden', locked);

  if (locked) {
    document.body.style.overflow = 'hidden';
  } else if (!isHeaderMobileMenuOpen()) {
    document.body.style.overflow = '';
  }
};

const syncModalHash = (itemId) => {
  const url = new URL(window.location.href);
  url.hash = itemId || '';
  history.replaceState(null, '', url);
};

const ensureModal = () => {
  let modalEl = document.getElementById('inspiration-gallery-modal');
  if (modalEl) {
    return modalEl;
  }

  modalEl = document.createElement('div');
  modalEl.id = 'inspiration-gallery-modal';
  modalEl.className = 'modal fade inspiration-gallery-modal';
  modalEl.tabIndex = -1;
  modalEl.setAttribute('aria-hidden', 'true');
  modalEl.innerHTML = `
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header border-0">
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body pt-0">
          <div class="position-relative inspiration-gallery-modal__stage">
            <img class="inspiration-gallery-modal__image img-fluid w-100" src="" alt="" decoding="async" fetchpriority="high" />
            <button
              type="button"
              class="carousel-control-prev inspiration-gallery-modal__nav"
              data-gallery-nav="prev"
              aria-label="Previous image"
            >
              <span class="carousel-control-prev-icon" aria-hidden="true"></span>
            </button>
            <button
              type="button"
              class="carousel-control-next inspiration-gallery-modal__nav"
              data-gallery-nav="next"
              aria-label="Next image"
            >
              <span class="carousel-control-next-icon" aria-hidden="true"></span>
            </button>
          </div>
          <p class="inspiration-gallery-modal__caption mt-3 mb-0"></p>
        </div>
      </div>
    </div>
  `;
  document.body.appendChild(modalEl);

  modalEl.addEventListener('hidden.bs.modal', () => {
    pendingModalItemId = null;
    syncModalHash('');
  });

  return modalEl;
};

const renderLoading = () => {
  const root = getRoot();
  if (!root) {
    return;
  }
  root.innerHTML = `
    <div class="inspiration-gallery-loading text-center py-5" role="status" aria-live="polite">
      <div class="spinner-border" aria-hidden="true"></div>
      <p class="mt-3 mb-0">Loading gallery…</p>
    </div>
  `;
};

const renderMessage = (html) => {
  const root = getRoot();
  if (!root) {
    return;
  }
  const content = html || 'No gallery images were found.';
  root.innerHTML = `
    <div class="container">
      <div class="inspiration-gallery-message alert alert-warning py-4" role="alert">
        <div class="body-sm">${content}</div>
      </div>
    </div>
  `;
};

const renderCards = items => (
  items.map((item) => `
    <div class="col">
      <button
        type="button"
        class="ratio ratio-1x1 p-0 border-0 bg-transparent overflow-hidden inspiration-gallery-item"
        data-gallery-id="${escapeHtml(item.id)}"
        aria-label="View details for ${escapeHtml(item.filename || item.id)}"
      >
        <img
          class="w-100 h-100 object-fit-cover inspiration-gallery-item__image"
          src="${escapeHtml(item.thumbUrl)}"
          alt=""
          loading="lazy"
          width="360"
          height="360"
        />
      </button>
    </div>
  `).join('')
);

const renderFilteredResults = (items) => {
  const region = document.getElementById('inspiration-gallery-grid-region');
  if (!region) {
    return;
  }

  const filteredItems = filterItems(items);
  if (!filteredItems.length) {
    region.innerHTML = `
      <div class="inspiration-gallery-message alert alert-warning" role="alert">
        <div class="body-sm">${window.noMatchesErrorMessage || 'No gallery images match those filters.'}</div>
      </div>
      <span class="visually-hidden" role="status">Gallery updated, 0 items shown</span>
    `;
    return;
  }

  region.innerHTML = `
    <div class="row row-cols-3 row-cols-sm-4 row-cols-md-5 g-1 inspiration-gallery-grid" id="inspiration-gallery-grid">
      ${renderCards(filteredItems)}
    </div>
    <span class="visually-hidden" role="status">Gallery updated, ${filteredItems.length} items shown</span>
  `;
};

const updateClearAllVisibility = () => {
  const show = hasSelectedFilters();
  document.querySelectorAll('[data-gallery-clear-filters]').forEach((clearBtn) => {
    clearBtn.classList.toggle('d-none', !show);
  });
};

const getSelectedFilterEntries = () => (
  FILTERS.flatMap(({ key }) => (
    (selectedFilters[key] || []).map(value => ({ key, value }))
  ))
);

const renderSelectedFilterChips = () => {
  const container = document.querySelector('[data-gallery-selected-filters]');
  if (!container) {
    return;
  }

  const clearBtn = container.querySelector('[data-gallery-clear-filters]');
  container.querySelectorAll('[data-gallery-remove-filter]').forEach((chip) => {
    chip.remove();
  });

  const selected = getSelectedFilterEntries();
  container.classList.toggle('d-none', selected.length === 0);

  selected.forEach(({ key, value }) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'btn btn-outline-body d-inline-flex align-items-center gap-2 inspiration-gallery-filter-btn inspiration-gallery-selected-filter';
    button.dataset.filterKey = key;
    button.dataset.filterValue = value;
    button.dataset.galleryRemoveFilter = '';
    button.setAttribute('aria-label', `Remove ${value} filter`);

    const removeIcon = document.createElement('span');
    removeIcon.className = 'inspiration-gallery-selected-filter__remove';
    removeIcon.setAttribute('aria-hidden', 'true');
    removeIcon.textContent = '\u00D7';

    const label = document.createElement('span');
    label.className = 'inspiration-gallery-selected-filter__label';
    label.textContent = value;

    button.append(removeIcon, label);

    if (clearBtn) {
      container.insertBefore(button, clearBtn);
    } else {
      container.appendChild(button);
    }
  });

  updateClearAllVisibility();
};

const syncDesktopCheckboxesFromApplied = () => {
  document.querySelectorAll('[data-filter-key]').forEach((input) => {
    const key = input.dataset.filterKey;
    input.checked = selectedFilters[key]?.includes(input.value) || false;
  });
};

const syncPendingFromApplied = () => {
  document.querySelectorAll('[data-filter-pending]').forEach((input) => {
    const key = input.dataset.filterPending;
    input.checked = selectedFilters[key]?.includes(input.value) || false;
  });
};

const buildFiltersFromPending = () => (
  FILTERS.reduce((next, { key }) => {
    next[key] = [...document.querySelectorAll(`[data-filter-pending="${key}"]:checked`)]
      .map(input => input.value);
    return next;
  }, {})
);

const syncDesktopFromPending = () => {
  document.querySelectorAll('[data-filter-pending]').forEach((pendingInput) => {
    const key = pendingInput.dataset.filterPending;
    document.querySelectorAll(`[data-filter-key="${key}"]`).forEach((desktopInput) => {
      if (desktopInput.value === pendingInput.value) {
        desktopInput.checked = pendingInput.checked;
      }
    });
  });
};

const closeFilterPanel = ({ discardPending = true, returnFocus = true } = {}) => {
  const panel = getFilterPanelEl();
  const trigger = document.getElementById('inspiration-gallery-filter-trigger');
  if (!panel) {
    return;
  }

  if (discardPending) {
    syncPendingFromApplied();
  }

  panel.classList.remove('toggled');
  panel.setAttribute('aria-hidden', 'true');
  panel.inert = true;
  trigger?.setAttribute('aria-expanded', 'false');
  setBodyScrollLocked(false);

  if (returnFocus) {
    trigger?.focus();
  }
};

const openFilterPanel = () => {
  const panel = getFilterPanelEl();
  const trigger = document.getElementById('inspiration-gallery-filter-trigger');
  if (!panel) {
    return;
  }

  syncPendingFromApplied();
  closeHeaderMobileMenu();
  panel.classList.add('toggled');
  panel.inert = false;
  panel.setAttribute('aria-hidden', 'false');
  trigger?.setAttribute('aria-expanded', 'true');
  setBodyScrollLocked(true);
  panel.querySelector('.inspiration-gallery-filter-panel-close')?.focus();
};


const isBelowBootstrapLg = () => {
  const lgMin = getComputedStyle(document.documentElement)
    .getPropertyValue('--bs-breakpoint-lg')
    .trim() || '992px';

  return !window.matchMedia(`(min-width: ${lgMin})`).matches;
};

const scrollGalleryToTopOnMobile = () => {
  if (!isBelowBootstrapLg()) {
    return;
  }

  const root = getRoot();
  if (!root) {
    return;
  }

  const headerHeight = document.querySelector('.header-new')?.offsetHeight || 0;
  const top = root.getBoundingClientRect().top + window.scrollY - headerHeight;
  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  window.scrollTo({ top: Math.max(top, 0), behavior });
};

const removeSelectedFilter = (key, value) => {
  setFilterSelected(key, value, false);

  document.querySelectorAll('[data-filter-key], [data-filter-pending]').forEach((input) => {
    const inputKey = input.dataset.filterKey || input.dataset.filterPending;
    if (inputKey === key && input.value === value) {
      input.checked = false;
    }
  });

  renderSelectedFilterChips();
  renderFilteredResults(window.MediaGalleryData?.mediaItems || []);
  scrollGalleryToTopOnMobile();
};

const applyPendingMobileFilters = () => {
  replaceSelectedFilters(buildFiltersFromPending());
  syncDesktopFromPending();
  renderSelectedFilterChips();
  renderFilteredResults(window.MediaGalleryData?.mediaItems || []);
  closeFilterPanel({ discardPending: false });
  scrollGalleryToTopOnMobile();
};

const applyClearedFilters = () => {
  clearAllFilters();
  document.querySelectorAll('[data-filter-key], [data-filter-pending]').forEach((input) => {
    input.checked = false;
  });
  renderSelectedFilterChips();
  renderFilteredResults(window.MediaGalleryData?.mediaItems || []);
  closeFilterPanel({ discardPending: false });
  scrollGalleryToTopOnMobile();
};

const filtersIconSvg = `
  <svg class="flex-shrink-0 inspiration-gallery-filter-trigger__icon" width="22" height="16" viewBox="10 5 22 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
    <line x1="11" y1="9" x2="31" y2="9" stroke="currentColor" stroke-width="2"></line>
    <line x1="11" y1="17" x2="31" y2="17" stroke="currentColor" stroke-width="2"></line>
    <circle cx="25" cy="9" r="2.25" fill="#fff" stroke="currentColor" stroke-width="1.5"></circle>
    <circle cx="17" cy="17" r="2.25" fill="#fff" stroke="currentColor" stroke-width="1.5"></circle>
  </svg>
`;

const renderDesktopOption = (key, option, index) => {
  const id = `gallery-filter-${key}-${index}`;
  const isChecked = selectedFilters[key].includes(option);
  return `
    <li>
      <label class="dropdown-item d-flex align-items-center gap-2" for="${id}">
        <input
          class="form-check-input m-0 flex-shrink-0"
          id="${id}"
          type="checkbox"
          value="${escapeHtml(option)}"
          data-filter-key="${key}"
          ${isChecked ? 'checked' : ''}
        />
        <span>${escapeHtml(option)}</span>
      </label>
    </li>
  `;
};

const renderPendingOption = (key, option, index) => {
  const id = `gallery-pending-${key}-${index}`;
  const isChecked = selectedFilters[key].includes(option);
  return `
    <div class="form-check">
      <input
        class="form-check-input"
        id="${id}"
        type="checkbox"
        value="${escapeHtml(option)}"
        data-filter-pending="${key}"
        ${isChecked ? 'checked' : ''}
      />
      <label class="form-check-label" for="${id}">${escapeHtml(option)}</label>
    </div>
  `;
};

const renderDesktopFilters = items => (
  FILTERS.map(({ key, label }) => {
    const options = filtersReady ? getFilterOptions(items, key) : [];
    const hasOptions = options.length > 0;
    const emptyText = filtersReady ? 'No options yet' : 'Loading filters…';

    const optionMarkup = hasOptions
      ? options.map((option, index) => renderDesktopOption(key, option, index)).join('')
      : `<li><span class="dropdown-item-text text-muted">${emptyText}</span></li>`;

    return `
      <div class="dropdown inspiration-gallery-filter">
        <button
          type="button"
          class="btn btn-taupe-25 dropdown-toggle inspiration-gallery-filter-btn"
          data-bs-toggle="dropdown"
          data-bs-auto-close="outside"
          data-filter-toggle="${key}"
          aria-expanded="false"
          ${hasOptions ? '' : 'disabled'}
        >
          ${escapeHtml(label)}
        </button>
        <ul class="dropdown-menu shadow-sm inspiration-gallery-filter__menu">
          ${optionMarkup}
        </ul>
      </div>
    `;
  }).join('')
);

const renderMobileAccordionGroups = items => (
  FILTERS.map(({ key, label }) => {
    const options = getFilterOptions(items, key);
    const collapseId = `gallery-mobile-filter-${key}`;
    const borderClass = 'border-bottom border-primary';

    const optionMarkup = options.length
      ? options.map((option, optionIndex) => renderPendingOption(key, option, optionIndex)).join('')
      : '<p class="text-muted mb-0">No options yet</p>';

    return `
      <div class="inspiration-gallery-filter-accordion-item ${borderClass}">
        <button
          type="button"
          class="btn btn-link text-decoration-none w-100 d-flex justify-content-between align-items-center py-3 px-0 inspiration-gallery-filter-accordion-btn"
          data-bs-toggle="collapse"
          data-bs-target="#${collapseId}"
          aria-expanded="false"
          aria-controls="${collapseId}"
        >
          ${escapeHtml(label)}
        </button>
        <div class="collapse" id="${collapseId}">
          <div class="pb-3 d-flex flex-column gap-2">
            ${optionMarkup}
          </div>
        </div>
      </div>
    `;
  }).join('')
);

const renderMobileFilterPanel = (items) => {
  let panel = getFilterPanelEl();
  if (!panel) {
    panel = document.createElement('div');
    panel.id = FILTER_PANEL_ID;
    panel.className = 'inspiration-gallery-filter-panel d-flex flex-column d-lg-none';
    panel.setAttribute('aria-hidden', 'true');
    panel.inert = true;
    panel.setAttribute('aria-labelledby', 'inspiration-gallery-filter-panel-title');
    panel.innerHTML = `
      <div class="inspiration-gallery-filter-panel-inner d-flex flex-column h-100 bg-white">
        <div class="inspiration-gallery-filter-panel-header flex-shrink-0 px-3 pt-3">
          <div class="d-flex justify-content-between align-items-center">
            <h2 class="mb-0" id="inspiration-gallery-filter-panel-title">Filters</h2>
            <button
              type="button"
              class="btn btn-link p-2 inspiration-gallery-filter-panel-close"
              aria-label="Close filters"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="1.375rem" height="1.375rem" fill="currentColor" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
                <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
              </svg>
            </button>
          </div>
          <hr class="mt-3 mb-0 border-2 border-top border-primary opacity-100" />
        </div>
        <div class="inspiration-gallery-filter-panel-scroll flex-grow-1 overflow-auto px-3">
          <div class="inspiration-gallery-filter-accordion-list"></div>
          <div class="pt-3 pb-5 text-center">
            <button
              type="button"
              id="inspiration-gallery-filter-apply"
              class="btn btn-primary text-uppercase inspiration-gallery-filter-apply px-5"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    `;
    document.body.appendChild(panel);
  }

  if (!filtersReady) {
    return;
  }

  const list = panel.querySelector('.inspiration-gallery-filter-accordion-list');
  if (list) {
    list.innerHTML = renderMobileAccordionGroups(items);
  }
};

const renderFilterBar = (items) => {
  const bar = document.getElementById('inspiration-gallery-filters');
  if (!bar) {
    return;
  }

  const isBuilt = Boolean(bar.querySelector('[data-filter-toggle]'));

  if (isBuilt && (filterBarRenderedReady || !filtersReady)) {
    return;
  }

  filterBarRenderedReady = filtersReady;

  const loadingStatus = filtersReady
    ? ''
    : '<span class="visually-hidden" role="status">Loading gallery filters</span>';

  bar.innerHTML = `
    ${loadingStatus}
    <div class="d-none d-lg-flex flex-wrap align-items-center gap-4">
      ${renderDesktopFilters(items)}
    </div>
    <div class="d-flex d-lg-none align-items-center gap-3 mt-3">
      <button
        type="button"
        id="inspiration-gallery-filter-trigger"
        class="btn d-inline-flex align-items-center gap-2 inspiration-gallery-filter-trigger"
        aria-controls="${FILTER_PANEL_ID}"
        aria-expanded="false"
        ${filtersReady ? '' : 'disabled'}
      >
        ${filtersIconSvg}
        <span class="text-secondary">Filters</span>
      </button>
    </div>
    <div
      id="inspiration-gallery-selected-filters"
      class="d-none d-flex flex-wrap align-items-center gap-2 mt-3"
      data-gallery-selected-filters
      aria-label="Selected filters"
    >
      <button
        type="button"
        data-gallery-clear-filters
        class="btn btn-link ms-3 p-0 border-0 text-decoration-underline text-nowrap inspiration-gallery-clear-filters d-none"
      >
        Clear All<span class="visually-hidden"> Gallery Filters</span>
      </button>
    </div>
  `;

  renderSelectedFilterChips();
  renderMobileFilterPanel(items);
};

const renderGrid = (items, { filtersComplete = false } = {}) => {
  const root = getRoot();
  if (!root) {
    return;
  }

  if (filtersComplete) {
    filtersReady = true;
  }

  if (!items.length) {
    renderMessage(window.noMatchesErrorMessage || 'No gallery images were found.');
    return;
  }

  const existingRegion = document.getElementById('inspiration-gallery-grid-region');
  if (!existingRegion) {
    root.innerHTML = `
      <div class="container pb-5">
        <div id="inspiration-gallery-filters" class="sticky-top mb-4 inspiration-gallery-filters"></div>
        <div id="inspiration-gallery-grid-region"></div>
      </div>
    `;
  }

  renderFilterBar(items);
  renderFilteredResults(items);
};

const preloadFullImage = (item) => {
  if (!item?.imageUrl) {
    return Promise.reject();
  }

  if (readyFullImages.has(item.id)) {
    return Promise.resolve();
  }

  const preload = new Image();
  preload.decoding = 'async';
  preload.src = item.imageUrl;

  const loaded = typeof preload.decode === 'function'
    ? preload.decode()
    : new Promise((resolve, reject) => {
      preload.addEventListener('load', resolve, { once: true });
      preload.addEventListener('error', reject, { once: true });
    });

  return loaded.then(() => {
    readyFullImages.add(item.id);
  });
};

const getVisibleItems = () => filterItems(window.MediaGalleryData?.mediaItems || []);

const getAdjacentItem = (direction) => {
  const items = getVisibleItems();
  if (items.length < 2) {
    return null;
  }

  const index = items.findIndex((entry) => entry.id === pendingModalItemId);
  if (index === -1) {
    return null;
  }

  return items[(index + direction + items.length) % items.length];
};

const openItem = (item) => {
  if (!item || typeof bootstrap === 'undefined') {
    return;
  }

  const modalEl = ensureModal();
  const image = modalEl.querySelector('.inspiration-gallery-modal__image');
  const caption = modalEl.querySelector('.inspiration-gallery-modal__caption');

  const hasFullImage = readyFullImages.has(item.id);
  pendingModalItemId = item.id;

  image.src = hasFullImage ? item.imageUrl : (item.thumbUrl || item.imageUrl);
  image.alt = item.description || item.filename || '';
  caption.textContent = item.description || '';

  const hasSiblings = getVisibleItems().length > 1;
  modalEl.querySelectorAll('[data-gallery-nav]').forEach((button) => {
    button.classList.toggle('d-none', !hasSiblings);
  });

  if (!hasFullImage) {
    preloadFullImage(item)
      .then(() => {
        if (pendingModalItemId === item.id) {
          image.src = item.imageUrl;
        }
      })
      .catch(() => {});
  }

  if (item.id) {
    syncModalHash(item.id);
  }

  const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
  modal.show();
};

const showAdjacentItem = (direction) => {
  const next = getAdjacentItem(direction);
  if (!next) {
    return;
  }

  openItem(next);

  const upcoming = getAdjacentItem(direction);
  if (upcoming) {
    preloadFullImage(upcoming).catch(() => {});
  }
};

const isModalOpen = () => (
  Boolean(document.getElementById('inspiration-gallery-modal')?.classList.contains('show'))
);

const bindModalNavigation = () => {
  const modalEl = ensureModal();

  modalEl.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gallery-nav]');
    if (!button) {
      return;
    }

    event.preventDefault();
    showAdjacentItem(button.dataset.galleryNav === 'prev' ? -1 : 1);
  });
};

const findItemFromEvent = (event) => {
  const button = event.target.closest('[data-gallery-id]');
  if (!button) {
    return null;
  }
  const id = button.getAttribute('data-gallery-id');
  const items = window.MediaGalleryData?.mediaItems || [];
  return items.find((entry) => entry.id === id) || null;
};

const bindGridClicks = () => {
  const root = getRoot();
  if (!root) {
    return;
  }

  bindModalNavigation();

  root.addEventListener('click', (event) => {
    const item = findItemFromEvent(event);
    if (item) {
      openItem(item);
    }
  });

  ['pointerover', 'focusin'].forEach((eventName) => {
    root.addEventListener(eventName, (event) => {
      const item = findItemFromEvent(event);
      if (item) {
        preloadFullImage(item).catch(() => {});
      }
    }, { passive: true });
  });
};

const bindOverlayMutualExclusion = () => {
  const menu = document.getElementById(HEADER_MOBILE_MENU_ID);

  if (menu) {
    menu.addEventListener('show.bs.collapse', () => {
      closeFilterPanel({ discardPending: true, returnFocus: false });
    });

    menu.addEventListener('hidden.bs.collapse', () => {
      if (isFilterPanelOpen()) {
        setBodyScrollLocked(true);
      }
    });
  }

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isFilterPanelOpen()) {
      event.preventDefault();
      closeFilterPanel();
      return;
    }

    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
      return;
    }

    if (!isModalOpen() || event.target.closest('input, textarea, select')) {
      return;
    }

    event.preventDefault();
    showAdjacentItem(event.key === 'ArrowLeft' ? -1 : 1);
  });
};

const bindFilterInteractions = () => {
  const root = getRoot();
  if (!root) {
    return;
  }

  root.addEventListener('change', (event) => {
    const input = event.target.closest('[data-filter-key]');
    if (!input) {
      return;
    }

    const key = input.dataset.filterKey;
    setFilterSelected(key, input.value, input.checked);
    renderSelectedFilterChips();
    syncPendingFromApplied();
    renderFilteredResults(window.MediaGalleryData?.mediaItems || []);
  });

  root.addEventListener('click', (event) => {
    if (event.target.closest('#inspiration-gallery-filter-trigger')) {
      event.preventDefault();
      if (isFilterPanelOpen()) {
        closeFilterPanel();
      } else {
        openFilterPanel();
      }
      return;
    }

    const chip = event.target.closest('[data-gallery-remove-filter]');
    if (chip) {
      event.preventDefault();
      removeSelectedFilter(chip.dataset.filterKey, chip.dataset.filterValue);
      return;
    }

    if (event.target.closest('[data-gallery-clear-filters]')) {
      event.preventDefault();
      applyClearedFilters();
      return;
    }

    if (event.target.closest('.inspiration-gallery-filter__menu')) {
      event.stopPropagation();
    }
  });

  document.addEventListener('click', (event) => {
    if (event.target.closest('#inspiration-gallery-filter-apply')) {
      event.preventDefault();
      applyPendingMobileFilters();
      return;
    }

    if (event.target.closest('.inspiration-gallery-filter-panel-close')) {
      event.preventDefault();
      closeFilterPanel();
    }
  });

  bindOverlayMutualExclusion();
};

const openFromHash = (items) => {
  const hash = window.location.hash.replace('#', '');
  if (!hash) {
    return;
  }
  const item = items.find((entry) => entry.id === hash);
  if (item) {
    openItem(item);
  }
};

export {
  renderLoading,
  renderGrid,
  renderMessage,
  bindGridClicks,
  bindFilterInteractions,
  openFromHash,
  openItem,
};
